import React, { useCallback, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import CustomerPageLayout from "../customer-page-layout/CustomerPageLayout";
import { PaymentProcessor, CustomerPinModal, TransactionResultPage } from "../components";
import { BulkEntry, BulkConfirmation, BulkTransactionPin, BulkSuccess } from "./component";
import styles from "./bulk-purchase.module.scss";

import { RootState } from "../../../stores/store";
import { CountryCode, countryCurrencies } from "../../../model/common/country";
import { BulkLineItem, lineItemsToRequest } from "../../../model/bulk-purchase/bulk-purchase-customer";
import { BulkPaymentMethod, BulkViewState, computeTotals } from "./bulk-purchase.types";
import { useValidateManualBulkPurchaseMutation } from "../../../service/bulk-purchase-service";
import { useGetWalletBalanceQuery } from "../../../service/charge-service";
import {
	clearBulkTransaction,
	setBulkPaymentMethod,
	setBulkTransactionDetails,
	setBulkTransactionReference,
	setBulkTransactionStatus,
} from "../../../stores/slices/bulk-purchase-slice";
import { getTransactionRefFromToken } from "../../../guards/common/authentification-functions";
import { getErrorMessage } from "../../../utils/error-handler";
import { formatCurrency } from "../../../utils";

export const BulkPurchasePage: React.FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [searchParams] = useSearchParams();

	const { countryCode } = useSelector((state: RootState) => state.user);
	const bulkTransaction = useSelector((state: RootState) => state.bulkPurchase);
	const { data: walletData } = useGetWalletBalanceQuery();
	const [validateManual, { isLoading: isValidating }] = useValidateManualBulkPurchaseMutation();

	const currency = countryCurrencies[countryCode as CountryCode] ?? "NGN";
	const hasWallet = !!walletData?.walletDetails?.length;
	const walletBalance = walletData?.walletDetails?.[0]?.availableBalance ?? 0;

	// Payment Hub (card) return: /bulk-purchase?token=... or ?reference=...
	const returnReference = useMemo(() => {
		const token = searchParams.get("token");
		if (token) return getTransactionRefFromToken(token) ?? null;
		return searchParams.get("reference");
	}, [searchParams]);

	const [items, setItems] = useState<BulkLineItem[]>([]);
	const [currentView, setCurrentView] = useState<BulkViewState>(returnReference ? "processing" : "entry");
	const [selectedMethod, setSelectedMethod] = useState<BulkPaymentMethod>("wallet");
	const [fee, setFee] = useState(0);
	const [topLevelErrors, setTopLevelErrors] = useState<string[]>([]);
	const [hasValidated, setHasValidated] = useState(false);
	const [showResetPin, setShowResetPin] = useState(false);
	const [reference, setReference] = useState<string | null>(returnReference);

	const totals = useMemo(() => computeTotals(items), [items]);
	const grandTotal = totals.total + fee;

	// --- item mutations ---
	const addItem = (item: BulkLineItem) => {
		setItems(prev => [...prev, item]);
		setHasValidated(false);
		setTopLevelErrors([]);
	};
	const updateItem = (item: BulkLineItem) => {
		setItems(prev => prev.map(i => (i.id === item.id ? { ...item, isValid: undefined, errors: [] } : i)));
		setHasValidated(false);
		setTopLevelErrors([]);
	};
	const deleteItem = (id: string) => {
		setItems(prev => prev.filter(i => i.id !== id));
		setTopLevelErrors([]);
	};
	const handleUploadResult = (uploaded: BulkLineItem[], errors: string[]) => {
		setItems(prev => [...prev, ...uploaded]);
		setTopLevelErrors(errors);
		setHasValidated(true);
	};

	// --- entry: validate then advance ---
	const handleContinue = useCallback(async () => {
		if (!items.length) {
			toast.error(t("buyElectricity.bulk.addAtLeastOneItem"));
			return;
		}
		try {
			const response = await validateManual(lineItemsToRequest(items)).unwrap();
			const rows = response.rows ?? [];
			const validated = items.map((item, idx) => {
				const row = rows[idx];
				return row ? { ...item, isValid: row.isValid, errors: row.errors ?? [] } : item;
			});
			setItems(validated);
			setTopLevelErrors(response.errors ?? []);
			setHasValidated(true);

			const allValid = response.invalidCount === 0 && (response.errors?.length ?? 0) === 0;
			if (allValid) {
				dispatch(
					setBulkTransactionDetails({
						total: totals.total,
						prepaidCount: totals.prepaidCount,
						postpaidCount: totals.postpaidCount,
					})
				);
				setCurrentView("confirmation");
			} else {
				toast.error(t("buyElectricity.bulk.bulkItemsValidationFailed"));
			}
		} catch (err: any) {
			toast.error(getErrorMessage(err?.data) ?? t("buyElectricity.bulk.bulkItemsValidationFailed"));
		}
	}, [items, validateManual, t, dispatch, totals]);

	// --- confirmation: pay ---
	const handlePay = (method: BulkPaymentMethod, calculatedFee: number) => {
		setSelectedMethod(method);
		setFee(calculatedFee);
		dispatch(
			setBulkTransactionDetails({
				total: totals.total,
				fee: calculatedFee,
				prepaidCount: totals.prepaidCount,
				postpaidCount: totals.postpaidCount,
			})
		);
		dispatch(setBulkPaymentMethod(method === "wallet" ? t("common.labels.wallet") : t("buyElectricity.web.debitCreditCard")));
		setCurrentView("pin");
	};

	// --- payment outcomes ---
	const handleSuccess = (ref: string) => {
		setReference(ref);
		dispatch(setBulkTransactionReference(ref));
		setCurrentView("success");
	};
	const handleFailed = (_ref: string) => {
		dispatch(setBulkTransactionStatus("failed"));
		setCurrentView("failed");
	};

	const goBack = useCallback(() => {
		if (currentView === "confirmation") setCurrentView("entry");
		else if (currentView === "pin") setCurrentView("confirmation");
		else navigate(-1);
	}, [currentView, navigate]);

	const continueLabel =
		hasValidated && (items.some(i => i.isValid === false) || topLevelErrors.length > 0)
			? t("buyElectricity.bulk.reValidate")
			: t("buyElectricity.bulk.continue");

	const walletBalanceFormatted = formatCurrency(walletBalance, { currency });

	return (
		<CustomerPageLayout
			title={t("buyElectricity.bulk.bulkPurchase")}
			onBack={goBack}
			showBackButton={currentView === "entry" || currentView === "confirmation" || currentView === "pin"}
			headerAction={
				currentView === "entry" || currentView === "confirmation" || currentView === "pin" ? (
					<div className={styles.walletPill}>
						<span className={styles.walletPillLabel}>{t("common.labels.wallet")}</span>
						<span className={styles.walletPillDivider} />
						<span className={styles.walletPillAmount}>{walletBalanceFormatted}</span>
					</div>
				) : undefined
			}
		>
			<div className={styles.pageContainer}>
				{currentView === "entry" && (
					<BulkEntry
						items={items}
						onAddItem={addItem}
						onUpdateItem={updateItem}
						onDeleteItem={deleteItem}
						onUploadResult={handleUploadResult}
						onContinue={handleContinue}
						continueLabel={continueLabel}
						isSubmitting={isValidating}
						topLevelErrors={topLevelErrors}
						currency={currency}
					/>
				)}

				{currentView === "confirmation" && (
					<BulkConfirmation
						total={totals.total}
						prepaidCount={totals.prepaidCount}
						postpaidCount={totals.postpaidCount}
						itemCount={items.length}
						currency={currency}
						hasWallet={hasWallet}
						walletBalance={walletBalance}
						onPay={handlePay}
					/>
				)}

				{currentView === "pin" && !showResetPin && (
					<BulkTransactionPin
						items={items}
						method={selectedMethod}
						payLabel={t("buyElectricity.bulk.payAmount", { amount: formatCurrency(grandTotal, { currency }) })}
						onBack={() => setCurrentView("confirmation")}
						onForgetPin={() => setShowResetPin(true)}
						onSuccess={handleSuccess}
						onFailed={handleFailed}
						onReferenceSet={ref => dispatch(setBulkTransactionReference(ref))}
					/>
				)}

				{currentView === "pin" && showResetPin && (
					<CustomerPinModal
						initialContent="resetPinContent"
						showResetSuccessScreen
						onClose={() => setShowResetPin(false)}
					/>
				)}

				{currentView === "processing" && reference && (
					<PaymentProcessor
						reference={reference}
						skipRedirect
						onSuccess={handleSuccess}
						onFailed={handleFailed}
						onReferenceSet={ref => dispatch(setBulkTransactionReference(ref))}
						statusMessages={{
							processing: { title: t("common.labels.vendingInProgress"), subtitle: t("payment.yourTokenIsOnItsWay") },
						}}
					/>
				)}

				{currentView === "success" && reference && (
					<BulkSuccess
						reference={reference}
						currency={currency}
						onDone={() => {
							dispatch(clearBulkTransaction());
							navigate("/dashboard");
						}}
					/>
				)}

				{currentView === "failed" && (
					<TransactionResultPage
						transaction={{
							status: (bulkTransaction.status as "failed" | "pending") || "failed",
							reference: bulkTransaction.reference || reference || t("buyElectricity.web.notAvailable"),
							amount: bulkTransaction.total ?? grandTotal,
							paymentMethod: bulkTransaction.paymentMethod || t("buyElectricity.web.notAvailable"),
							date: bulkTransaction.date || new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
							time: bulkTransaction.time || new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true }),
						}}
						onRetry={() => {
							dispatch(clearBulkTransaction());
							navigate(`/bulk-purchase`);
						}}
						onDone={() => {
							dispatch(clearBulkTransaction());
							navigate("/dashboard");
						}}
					/>
				)}
			</div>
		</CustomerPageLayout>
	);
};

export default BulkPurchasePage;
